<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, ref } from 'vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import {
  INPUT_CLASS as inputClass,
  SELECT_CLASS as selectClass,
  TEXTAREA_CLASS as textareaClass,
} from '../utils/pendentesConstants';
import { avatarText } from '../utils/pendentesFormatters';
import { formatPercent } from '../utils/pendentesOptionists';
import { usePendentesLaudoRegistration } from '../composables/usePendentesLaudoRegistration';

const props = defineProps({
  currentUser: { type: Object, default: null },
  actorPayload: { type: Object, default: () => ({}) },
  afterRegister: { type: Function, default: () => Promise.resolve() },
});

const registeringItemId = defineModel('registeringItemId', {
  type: String,
  default: '',
});

const dialogRef = ref(null);

const {
  registerFields,
  registerOptions,
  isLoadingRegistrationPreview,
  registrationPreviewError,
  activeMediaImageIndex,
  activeRegistrationStep,
  isReadingMediaFiles,
  optionistSearch,
  isLoadingOptionists,
  selectedPrimaryOptionistId,
  manualOptionistPercentages,
  isRegisteringLaudo,
  registerTargetItem,
  registerTargetTitle,
  registerTargetSubtitle,
  registrationMediaImages,
  activeMediaImage,
  registerSourceUrl,
  registerSourcePortal,
  mediaImageCounterLabel,
  registrationSteps,
  activeRegistrationStepIndex,
  activeRegistrationStepConfig,
  isFirstRegistrationStep,
  isLastRegistrationStep,
  primaryOptionistOptions,
  selectedPrimaryOptionist,
  filteredExtraOptionists,
  optionistAllocationPreview,
  optionistAllocationErrors,
  visibleOptionistAllocationErrors,
  registrationFieldErrors,
  visibleRegistrationErrors,
  canConfirmLaudoRegistration,
  registrationStepErrors,
  activeRegistrationStepErrors,
  firstRegistrationIssueStep,
  registrationReviewCards,
  selectMediaImage,
  nextMediaImage,
  previousMediaImage,
  removeMediaImage,
  addMediaImagesFromFiles,
  updateActiveMediaImageCaption,
  setRegistrationStep,
  goToNextRegistrationStep,
  goToPreviousRegistrationStep,
  goToFirstRegistrationIssue,
  isExtraSelected,
  toggleExtraOptionist,
  setOptionistPercentage,
  isOptionistPercentageEditable,
  prepareRegistration,
  loadRegistration,
  resetRegistration,
  confirmLaudoRegistration,
} = usePendentesLaudoRegistration({
  currentUser: computed(() => props.currentUser),
  actorPayload: computed(() => props.actorPayload),
  registeringLaudoItemId: registeringItemId,
  closeDialog: () => dialogRef.value?.close(),
  afterRegister: payload => props.afterRegister(payload),
});

function cancelRegisterDialog() {
  if (isRegisteringLaudo.value) return;
  dialogRef.value?.close();
}

defineExpose({
  open: item => {
    prepareRegistration(item);
    dialogRef.value?.open();
    return loadRegistration(item);
  },
  close: () => dialogRef.value?.close(),
});
</script>

<template>
  <Dialog
    ref="dialogRef"
    title="Cadastrar laudo no Midas"
    confirm-button-label="Cadastrar no Midas"
    cancel-button-label="Cancelar"
    width="5xl"
    position="top"
    overflow-y-auto
    :is-loading="isRegisteringLaudo"
    :disable-confirm-button="!canConfirmLaudoRegistration"
    @confirm="confirmLaudoRegistration"
    @close="resetRegistration"
  >
    <div
      class="max-h-[calc(100vh-11rem)] space-y-4 overflow-y-auto overscroll-contain pr-1 text-[13px] text-lais-text sm:max-h-[calc(100vh-13rem)]"
    >
      <div class="grid gap-4 xl:grid-cols-[minmax(0,1fr)_20rem]">
        <div
          class="overflow-hidden rounded-lg border border-lais-border bg-white shadow-lais-soft"
        >
          <div
            class="relative min-h-[220px] overflow-hidden bg-lais-primary-deep sm:min-h-[300px]"
          >
            <img
              v-if="activeMediaImage"
              :src="activeMediaImage.url"
              :alt="activeMediaImage.caption || 'Foto do imóvel'"
              class="absolute inset-0 size-full object-cover"
            />
            <div
              v-else
              class="absolute inset-0 grid place-items-center bg-lais-primary-deep text-center text-white"
            >
              <span
                class="i-lucide-image-plus mx-auto size-10 opacity-80"
                aria-hidden="true"
              />
              <p class="mt-3 text-[13px] font-bold">
                Adicione as fotos do anúncio
              </p>
            </div>
            <div
              class="absolute inset-0 bg-gradient-to-b from-black/70 via-black/10 to-black/70"
            />
            <div class="absolute inset-x-0 top-0 flex items-start gap-3 p-4">
              <span
                class="grid size-12 shrink-0 place-items-center rounded-full bg-white text-[13px] font-bold text-woot-700 ring-1 ring-white/50"
              >
                {{ avatarText(registerTargetItem || {}) }}
              </span>
              <div class="min-w-0 flex-1 text-white">
                <p class="text-[11px] font-bold uppercase opacity-80">
                  Novo laudo
                </p>
                <p class="mt-1 truncate text-[15px] font-bold">
                  {{ registerTargetTitle }}
                </p>
                <p
                  v-if="registerTargetSubtitle"
                  class="mt-0.5 truncate text-[12px] font-semibold opacity-80"
                >
                  {{ registerTargetSubtitle }}
                </p>
              </div>
              <span
                class="inline-flex shrink-0 items-center gap-1 rounded-full bg-white/95 px-2.5 py-1 text-[11px] font-bold text-woot-700"
              >
                <span
                  class="i-lucide-file-plus-2 size-3.5"
                  aria-hidden="true"
                />
                MIDAS
              </span>
            </div>
            <div class="absolute inset-x-0 bottom-0 space-y-3 p-4">
              <div class="flex flex-wrap gap-2">
                <span
                  v-if="registerFields.salePrice"
                  class="inline-flex items-center gap-1 rounded-full bg-white/95 px-2.5 py-1 text-[11px] font-bold text-green-700"
                >
                  <span
                    class="i-lucide-badge-dollar-sign size-3.5"
                    aria-hidden="true"
                  />
                  {{ registerFields.salePrice }}
                </span>
                <span
                  v-if="registerFields.propertyTypeLabel"
                  class="inline-flex items-center gap-1 rounded-full bg-white/95 px-2.5 py-1 text-[11px] font-bold text-slate-700"
                >
                  <span class="i-lucide-house size-3.5" aria-hidden="true" />
                  {{ registerFields.propertyTypeLabel }}
                </span>
                <span
                  class="inline-flex items-center gap-1 rounded-full bg-white/95 px-2.5 py-1 text-[11px] font-bold text-slate-700"
                >
                  <span class="i-lucide-images size-3.5" aria-hidden="true" />
                  {{ mediaImageCounterLabel }}
                </span>
              </div>
              <a
                v-if="registerSourceUrl"
                :href="registerSourceUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex max-w-full items-center gap-1.5 rounded-full bg-white/95 px-2.5 py-1 text-[11px] font-bold text-woot-700 transition hover:bg-woot-50"
              >
                <span
                  class="i-lucide-external-link size-3.5 shrink-0"
                  aria-hidden="true"
                />
                <span class="shrink-0">
                  {{ registerSourcePortal || 'Anúncio concorrente' }}
                </span>
                <span class="truncate font-semibold text-lais-text-muted">
                  {{ registerSourceUrl }}
                </span>
              </a>
            </div>
          </div>
        </div>

        <aside
          class="rounded-lg border border-lais-border bg-white p-4 shadow-lais-soft xl:self-start"
        >
          <div class="flex items-center justify-between gap-3">
            <div>
              <p class="text-[11px] font-bold uppercase text-lais-text-muted">
                Revisão rápida
              </p>
              <p class="mt-1 text-[14px] font-bold text-lais-text">
                {{ activeRegistrationStepConfig.label }}
              </p>
            </div>
            <span
              class="grid size-10 place-items-center rounded-full bg-lais-primary-50 text-lais-primary ring-1 ring-lais-primary-50"
            >
              <span
                :class="activeRegistrationStepConfig.icon"
                class="size-4"
                aria-hidden="true"
              />
            </span>
          </div>

          <div class="mt-4 grid gap-2">
            <button
              v-for="card in registrationReviewCards"
              :key="card.key"
              type="button"
              class="flex w-full items-center gap-3 rounded-lg border border-lais-border bg-lais-bg-page px-3 py-2 text-left transition hover:border-lais-primary"
              @click="setRegistrationStep(card.step)"
            >
              <span
                class="grid size-8 shrink-0 place-items-center rounded-full ring-1"
                :class="card.tone"
              >
                <span :class="card.icon" class="size-4" aria-hidden="true" />
              </span>
              <span class="min-w-0 flex-1">
                <span
                  class="block text-[11px] font-bold uppercase text-lais-text-muted"
                >
                  {{ card.label }}
                </span>
                <span
                  class="block truncate text-[13px] font-bold text-lais-text"
                >
                  {{ card.value }}
                </span>
              </span>
              <span
                class="i-lucide-chevron-right size-4 shrink-0 text-lais-text-muted"
                aria-hidden="true"
              />
            </button>
          </div>

          <div
            class="mt-4 rounded-lg border border-lais-border bg-lais-bg-page px-3 py-3"
          >
            <p class="text-[11px] font-bold uppercase text-lais-text-muted">
              Principal
            </p>
            <p class="mt-1 truncate text-[13px] font-bold text-lais-text">
              {{
                selectedPrimaryOptionist
                  ? selectedPrimaryOptionist.name
                  : 'Nenhum corretor escolhido'
              }}
            </p>
            <p class="mt-1 text-[12px] font-semibold text-lais-text-muted">
              {{ optionistAllocationPreview.length }} pessoa(s) na divisão
            </p>
          </div>
        </aside>
      </div>

      <div
        v-if="isLoadingRegistrationPreview"
        class="flex items-center gap-2 rounded-lais border border-lais-border bg-white px-4 py-3 text-[12px] font-semibold text-lais-text-muted"
      >
        <span
          class="i-lucide-loader-circle size-4 animate-spin text-lais-primary"
          aria-hidden="true"
        />
        Carregando ficha do Midas...
      </div>

      <div
        v-if="registrationPreviewError"
        class="rounded-lais border border-yellow-100 bg-yellow-50 px-4 py-3 text-[12px] font-semibold text-yellow-800"
      >
        {{ registrationPreviewError }}
      </div>

      <div
        v-if="registrationFieldErrors.length"
        class="rounded-lais border border-red-100 bg-red-50 px-4 py-3 text-[12px] font-semibold text-red-700"
      >
        <div class="flex gap-2">
          <span
            class="i-lucide-circle-alert mt-0.5 size-4 shrink-0"
            aria-hidden="true"
          />
          <div class="space-y-1">
            <p v-for="error in visibleRegistrationErrors" :key="error">
              {{ error }}
            </p>
            <p v-if="registrationFieldErrors.length > 3">
              + {{ registrationFieldErrors.length - 3 }} ajuste(s)
            </p>
          </div>
        </div>
      </div>

      <div
        class="rounded-lg border border-lais-border bg-white p-2 shadow-lais-soft"
      >
        <div
          class="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6"
          role="tablist"
          aria-label="Etapas do cadastro do laudo"
        >
          <button
            v-for="(step, index) in registrationSteps"
            :key="step.key"
            type="button"
            class="flex min-h-12 min-w-0 items-center gap-2 rounded-lg border px-3 py-2 text-left transition"
            :class="
              activeRegistrationStep === step.key
                ? 'border-lais-primary bg-lais-primary-50 text-lais-primary'
                : 'border-transparent bg-lais-bg-page text-lais-text-muted hover:border-lais-border hover:bg-white'
            "
            role="tab"
            :aria-selected="activeRegistrationStep === step.key"
            @click="setRegistrationStep(step.key)"
          >
            <span
              class="grid size-7 shrink-0 place-items-center rounded-full bg-white ring-1 ring-lais-border"
            >
              <span :class="step.icon" class="size-3.5" aria-hidden="true" />
            </span>
            <span class="min-w-0 flex-1">
              <span class="block truncate text-[12px] font-bold">
                {{ step.shortLabel }}
              </span>
              <span class="block text-[10px] font-bold opacity-70">
                {{ index + 1 }}/{{ registrationSteps.length }}
              </span>
            </span>
            <span
              v-if="registrationStepErrors[step.key]?.length"
              class="grid min-w-5 place-items-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white"
            >
              {{ registrationStepErrors[step.key].length }}
            </span>
          </button>
        </div>
      </div>

      <div
        v-if="activeRegistrationStepErrors.length"
        class="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-[12px] font-semibold text-red-700"
      >
        <div class="flex gap-2">
          <span
            class="i-lucide-circle-alert mt-0.5 size-4 shrink-0"
            aria-hidden="true"
          />
          <div class="space-y-1">
            <p
              v-for="error in activeRegistrationStepErrors.slice(0, 3)"
              :key="error"
            >
              {{ error }}
            </p>
          </div>
        </div>
      </div>

      <section
        v-show="activeRegistrationStep === 'media'"
        class="rounded-lg border border-lais-border bg-white p-4"
      >
        <div
          class="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
        >
          <div class="flex items-center gap-2">
            <span
              class="grid size-8 place-items-center rounded-full bg-woot-50 text-woot-700 ring-1 ring-woot-100"
            >
              <span class="i-lucide-images size-4" aria-hidden="true" />
            </span>
            <div>
              <h3 class="text-[13px] font-bold text-lais-text">
                Fotos do imóvel
              </h3>
              <p class="text-[11px] font-semibold text-lais-text-muted">
                {{ registrationMediaImages.length }} imagem(ns) para enviar ao
                Midas
              </p>
            </div>
          </div>
          <span
            class="w-fit rounded-full bg-lais-bg-page px-2.5 py-1 text-[11px] font-bold text-lais-text-muted"
          >
            {{ mediaImageCounterLabel }}
          </span>
        </div>

        <div
          v-if="activeMediaImage"
          class="overflow-hidden rounded-lais border border-lais-border bg-lais-bg-page"
        >
          <div class="relative aspect-[16/10] bg-slate-100">
            <img
              :src="activeMediaImage.url"
              :alt="activeMediaImage.caption || 'Foto do imóvel'"
              class="size-full object-cover"
            />
            <div
              class="absolute inset-x-0 top-0 flex items-center justify-between bg-gradient-to-b from-black/45 to-transparent p-3 text-white"
            >
              <button
                type="button"
                class="grid size-9 place-items-center rounded-full bg-black/35 text-white transition hover:bg-black/55"
                :disabled="registrationMediaImages.length < 2"
                @click="previousMediaImage"
              >
                <span class="i-lucide-chevron-left size-4" aria-hidden="true" />
                <span class="sr-only">Foto anterior</span>
              </button>
              <span class="rounded-full bg-black/35 px-3 py-1 text-[12px]">
                {{ mediaImageCounterLabel }}
              </span>
              <button
                type="button"
                class="grid size-9 place-items-center rounded-full bg-black/35 text-white transition hover:bg-black/55"
                :disabled="registrationMediaImages.length < 2"
                @click="nextMediaImage"
              >
                <span
                  class="i-lucide-chevron-right size-4"
                  aria-hidden="true"
                />
                <span class="sr-only">Próxima foto</span>
              </button>
            </div>
            <button
              type="button"
              class="absolute bottom-3 right-3 inline-flex h-9 items-center gap-2 rounded-full bg-white px-3 text-[12px] font-bold text-red-600 shadow-lais-soft transition hover:bg-red-50"
              @click="removeMediaImage(activeMediaImageIndex)"
            >
              <span class="i-lucide-trash-2 size-3.5" aria-hidden="true" />
              Remover
            </button>
          </div>
          <div class="grid gap-3 border-t border-lais-border bg-white p-3">
            <label class="grid gap-1">
              <span class="text-[11px] font-semibold text-lais-text-muted">
                Legenda
              </span>
              <input
                :value="activeMediaImage.caption"
                :class="inputClass"
                placeholder="Ex.: Sala, quarto, fachada"
                @input="updateActiveMediaImageCaption($event.target.value)"
              />
            </label>
            <div class="flex gap-2 overflow-x-auto pb-1 no-scrollbar">
              <button
                v-for="(image, index) in registrationMediaImages"
                :key="image.url"
                type="button"
                class="relative h-20 w-28 shrink-0 overflow-hidden rounded-lais-sm border transition"
                :class="
                  activeMediaImageIndex === index
                    ? 'border-lais-primary ring-2 ring-lais-primary-50'
                    : 'border-lais-border hover:border-lais-primary'
                "
                @click="selectMediaImage(index)"
              >
                <img
                  :src="image.url"
                  :alt="image.caption || 'Miniatura do imóvel'"
                  class="size-full object-cover"
                />
                <span
                  class="absolute left-1 top-1 rounded-full bg-black/55 px-1.5 py-0.5 text-[10px] font-bold text-white"
                >
                  {{ index + 1 }}
                </span>
              </button>
            </div>
          </div>
        </div>

        <div
          v-else
          class="grid place-items-center rounded-lais border border-dashed border-lais-border bg-lais-bg-page px-4 py-8 text-center"
        >
          <span
            class="i-lucide-image-plus mx-auto size-8 text-lais-text-muted"
            aria-hidden="true"
          />
          <p class="mt-2 text-[13px] font-semibold text-lais-text">
            Nenhuma foto selecionada
          </p>
        </div>

        <label
          class="mt-3 flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lais border border-dashed border-woot-200 bg-woot-50 px-4 py-4 text-center text-woot-800 transition hover:border-woot-500 hover:bg-woot-100"
        >
          <span
            class="grid size-10 place-items-center rounded-full bg-white text-woot-700 ring-1 ring-woot-100"
          >
            <span
              class="i-lucide-upload-cloud size-5"
              :class="isReadingMediaFiles ? 'animate-pulse' : ''"
              aria-hidden="true"
            />
          </span>
          <span class="text-[13px] font-bold">
            {{
              isReadingMediaFiles
                ? 'Preparando fotos...'
                : 'Enviar fotos do computador'
            }}
          </span>
          <span class="text-[11px] font-semibold text-woot-700/80">
            JPG/JPEG até 8 MB por foto
          </span>
          <input
            type="file"
            multiple
            accept="image/jpeg,.jpg,.jpeg"
            class="sr-only"
            :disabled="isReadingMediaFiles"
            @change="addMediaImagesFromFiles"
          />
        </label>
      </section>

      <section
        v-show="activeRegistrationStep === 'identity'"
        class="rounded-lg border border-lais-border bg-white p-4"
      >
        <div class="mb-3 flex items-center gap-2">
          <span
            class="grid size-8 place-items-center rounded-full bg-woot-50 text-woot-700 ring-1 ring-woot-100"
          >
            <span class="i-lucide-user-round size-4" aria-hidden="true" />
          </span>
          <h3 class="text-[13px] font-bold text-lais-text">
            Proprietário e contato
          </h3>
        </div>
        <div class="grid gap-3 md:grid-cols-3">
          <label class="grid gap-1 md:col-span-3">
            <span class="text-[11px] font-semibold text-lais-text-muted">
              Nome do proprietário
            </span>
            <input v-model="registerFields.ownerName" :class="inputClass" />
          </label>
          <label class="grid gap-1">
            <span class="text-[11px] font-semibold text-lais-text-muted">
              Telefone
            </span>
            <input
              v-model="registerFields.ownerPhone"
              :class="inputClass"
              inputmode="tel"
            />
          </label>
          <label class="grid gap-1">
            <span class="text-[11px] font-semibold text-lais-text-muted">
              E-mail
            </span>
            <input
              v-model="registerFields.ownerEmail"
              :class="inputClass"
              inputmode="email"
            />
          </label>
          <label class="grid gap-1">
            <span class="text-[11px] font-semibold text-lais-text-muted">
              Forma de contato
            </span>
            <select v-model="registerFields.contactFormId" :class="selectClass">
              <option
                v-for="option in registerOptions.contactForms"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </label>
          <label class="grid gap-1 md:col-span-3">
            <span class="text-[11px] font-semibold text-lais-text-muted">
              Mídia de divulgação
            </span>
            <select
              v-model="registerFields.mediaDisclosureId"
              :class="selectClass"
            >
              <option
                v-for="option in registerOptions.mediaDisclosures"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </label>
        </div>
      </section>

      <section
        v-show="activeRegistrationStep === 'property'"
        class="rounded-lg border border-lais-border bg-white p-4"
      >
        <div class="mb-3 flex items-center gap-2">
          <span
            class="grid size-8 place-items-center rounded-full bg-green-50 text-green-700 ring-1 ring-green-100"
          >
            <span class="i-lucide-map-pin-house size-4" aria-hidden="true" />
          </span>
          <h3 class="text-[13px] font-bold text-lais-text">
            Imóvel e endereço
          </h3>
        </div>
        <div class="grid gap-3 md:grid-cols-4">
          <label class="grid gap-1 md:col-span-2">
            <span class="text-[11px] font-semibold text-lais-text-muted">
              Tipo do imóvel
            </span>
            <select
              v-model="registerFields.propertyTypeId"
              :class="selectClass"
            >
              <option value="">Selecione</option>
              <option
                v-for="option in registerOptions.propertyTypes"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </label>
          <label class="grid gap-1">
            <span class="text-[11px] font-semibold text-lais-text-muted">
              CEP
            </span>
            <input
              v-model="registerFields.postalCode"
              :class="inputClass"
              inputmode="numeric"
            />
          </label>
          <label class="grid gap-1">
            <span class="text-[11px] font-semibold text-lais-text-muted">
              Número
            </span>
            <input v-model="registerFields.streetNumber" :class="inputClass" />
          </label>
          <label class="grid gap-1 md:col-span-3">
            <span class="text-[11px] font-semibold text-lais-text-muted">
              Logradouro
            </span>
            <input v-model="registerFields.address" :class="inputClass" />
          </label>
          <label class="grid gap-1">
            <span class="text-[11px] font-semibold text-lais-text-muted">
              Bairro
            </span>
            <input v-model="registerFields.neighbourhood" :class="inputClass" />
          </label>
          <label class="grid gap-1">
            <span class="text-[11px] font-semibold text-lais-text-muted">
              Cidade
            </span>
            <input v-model="registerFields.city" :class="inputClass" />
          </label>
          <label class="grid gap-1 md:col-span-3">
            <span class="text-[11px] font-semibold text-lais-text-muted">
              Complemento original
            </span>
            <input v-model="registerFields.complement" :class="inputClass" />
          </label>
          <label class="grid gap-1">
            <span class="text-[11px] font-semibold text-lais-text-muted">
              Tipo comp. 1
            </span>
            <select
              v-model="registerFields.complementTypeId"
              :class="selectClass"
            >
              <option value="">Sem tipo</option>
              <option
                v-for="option in registerOptions.complementTypes"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </label>
          <label class="grid gap-1 md:col-span-2">
            <span class="text-[11px] font-semibold text-lais-text-muted">
              Texto comp. 1
            </span>
            <input
              v-model="registerFields.complementText"
              :class="inputClass"
            />
          </label>
          <label class="grid gap-1">
            <span class="text-[11px] font-semibold text-lais-text-muted">
              Tipo comp. 2
            </span>
            <select
              v-model="registerFields.complement2TypeId"
              :class="selectClass"
            >
              <option value="">Sem tipo</option>
              <option
                v-for="option in registerOptions.complementTypes"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </label>
          <label class="grid gap-1 md:col-span-2">
            <span class="text-[11px] font-semibold text-lais-text-muted">
              Texto comp. 2
            </span>
            <input
              v-model="registerFields.complement2Text"
              :class="inputClass"
            />
          </label>
        </div>
      </section>

      <section
        v-show="activeRegistrationStep === 'pricing'"
        class="rounded-lg border border-lais-border bg-white p-4"
      >
        <div class="mb-3 flex items-center gap-2">
          <span
            class="grid size-8 place-items-center rounded-full bg-lais-primary-50 text-lais-primary ring-1 ring-lais-primary-50"
          >
            <span
              class="i-lucide-badge-dollar-sign size-4"
              aria-hidden="true"
            />
          </span>
          <h3 class="text-[13px] font-bold text-lais-text">
            Valores e características
          </h3>
        </div>
        <div class="grid gap-3 md:grid-cols-4">
          <label class="grid gap-1 md:col-span-2">
            <span class="text-[11px] font-semibold text-lais-text-muted">
              Finalidade
            </span>
            <select v-model="registerFields.objective" :class="selectClass">
              <option value="compra">Compra</option>
              <option value="locacao">Locação</option>
            </select>
          </label>
          <label class="grid gap-1 md:col-span-2">
            <span class="text-[11px] font-semibold text-lais-text-muted">
              Status de venda
            </span>
            <select v-model="registerFields.saleStatusId" :class="selectClass">
              <option
                v-for="option in registerOptions.saleStatuses"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </label>
          <label class="grid gap-1 md:col-span-2">
            <span class="text-[11px] font-semibold text-lais-text-muted">
              Valor de venda
            </span>
            <input
              v-model="registerFields.salePrice"
              :class="inputClass"
              inputmode="decimal"
            />
          </label>
          <label class="grid gap-1">
            <span class="text-[11px] font-semibold text-lais-text-muted">
              Quartos
            </span>
            <input
              v-model="registerFields.bedrooms"
              :class="inputClass"
              min="0"
              type="number"
            />
          </label>
          <label class="grid gap-1">
            <span class="text-[11px] font-semibold text-lais-text-muted">
              Banheiros
            </span>
            <input
              v-model="registerFields.bathrooms"
              :class="inputClass"
              min="0"
              type="number"
            />
          </label>
          <label class="grid gap-1">
            <span class="text-[11px] font-semibold text-lais-text-muted">
              Vagas
            </span>
            <input
              v-model="registerFields.parkingSpaces"
              :class="inputClass"
              min="0"
              type="number"
            />
          </label>
          <label class="grid gap-1">
            <span class="text-[11px] font-semibold text-lais-text-muted">
              Salas
            </span>
            <input
              v-model="registerFields.livingRooms"
              :class="inputClass"
              min="0"
              type="number"
            />
          </label>
          <label class="grid gap-1">
            <span class="text-[11px] font-semibold text-lais-text-muted">
              Suítes
            </span>
            <input
              v-model="registerFields.suites"
              :class="inputClass"
              min="0"
              type="number"
            />
          </label>
          <label class="grid gap-1">
            <span class="text-[11px] font-semibold text-lais-text-muted">
              Área útil
            </span>
            <input
              v-model="registerFields.usefulArea"
              :class="inputClass"
              inputmode="decimal"
            />
          </label>
          <label class="grid gap-1">
            <span class="text-[11px] font-semibold text-lais-text-muted">
              Área total
            </span>
            <input
              v-model="registerFields.totalArea"
              :class="inputClass"
              inputmode="decimal"
            />
          </label>
          <label class="grid gap-1">
            <span class="text-[11px] font-semibold text-lais-text-muted">
              Área do terreno
            </span>
            <input
              v-model="registerFields.landArea"
              :class="inputClass"
              inputmode="decimal"
            />
          </label>
          <label class="grid gap-1">
            <span class="text-[11px] font-semibold text-lais-text-muted">
              Área externa
            </span>
            <input
              v-model="registerFields.externalArea"
              :class="inputClass"
              inputmode="decimal"
            />
          </label>
          <label class="grid gap-1">
            <span class="text-[11px] font-semibold text-lais-text-muted">
              Condomínio
            </span>
            <input
              v-model="registerFields.condominiumFee"
              :class="inputClass"
              inputmode="decimal"
            />
          </label>
          <label class="grid gap-1">
            <span class="text-[11px] font-semibold text-lais-text-muted">
              IPTU
            </span>
            <input
              v-model="registerFields.iptuValue"
              :class="inputClass"
              inputmode="decimal"
            />
          </label>
          <label
            class="flex h-10 items-center gap-2 self-end rounded-lais-sm border border-lais-border bg-white px-3 text-[13px] font-semibold text-lais-text"
          >
            <input
              v-model="registerFields.furnished"
              type="checkbox"
              class="size-4 rounded border-lais-border text-lais-primary focus:ring-lais-primary"
            />
            Mobiliado
          </label>
          <label class="grid gap-1 md:col-span-2">
            <span class="text-[11px] font-semibold text-lais-text-muted">
              Tipo de opção
            </span>
            <select v-model="registerFields.optionTypeId" :class="selectClass">
              <option
                v-for="option in registerOptions.optionTypes"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </label>
          <label class="grid gap-1">
            <span class="text-[11px] font-semibold text-lais-text-muted">
              Classe do laudo
            </span>
            <input v-model="registerFields.laudoClassId" :class="inputClass" />
          </label>
        </div>
      </section>

      <section
        v-show="activeRegistrationStep === 'publication'"
        class="rounded-lg border border-lais-border bg-white p-4"
      >
        <div class="mb-3 flex items-center gap-2">
          <span
            class="grid size-8 place-items-center rounded-full bg-green-50 text-green-700 ring-1 ring-green-100"
          >
            <span class="i-lucide-file-text size-4" aria-hidden="true" />
          </span>
          <h3 class="text-[13px] font-bold text-lais-text">
            Descrição e publicação
          </h3>
        </div>
        <div class="grid gap-3 md:grid-cols-2">
          <label class="grid gap-1 md:col-span-2">
            <span class="text-[11px] font-semibold text-lais-text-muted">
              Título do anúncio
            </span>
            <input v-model="registerFields.adTitle" :class="inputClass" />
          </label>
          <label class="grid gap-1 md:col-span-2">
            <span class="text-[11px] font-semibold text-lais-text-muted">
              Descrição do imóvel
            </span>
            <textarea
              v-model="registerFields.propertyDescription"
              :class="textareaClass"
            />
          </label>
          <label class="grid gap-1">
            <span class="text-[11px] font-semibold text-lais-text-muted">
              Descrição para slide
            </span>
            <textarea
              v-model="registerFields.slideDescription"
              :class="textareaClass"
            />
          </label>
          <label class="grid gap-1">
            <span class="text-[11px] font-semibold text-lais-text-muted">
              Amenidades/destaques
            </span>
            <textarea
              v-model="registerFields.amenitiesText"
              :class="textareaClass"
            />
          </label>
        </div>
      </section>

      <section
        v-show="activeRegistrationStep === 'publication'"
        class="rounded-lg border border-lais-border bg-white p-4"
      >
        <div class="mb-3 flex items-center gap-2">
          <span
            class="grid size-8 place-items-center rounded-full bg-yellow-50 text-yellow-800 ring-1 ring-yellow-100"
          >
            <span class="i-lucide-sticky-note size-4" aria-hidden="true" />
          </span>
          <h3 class="text-[13px] font-bold text-lais-text">
            Observações do Midas
          </h3>
        </div>
        <div class="grid gap-3 md:grid-cols-2">
          <label class="grid gap-1">
            <span class="text-[11px] font-semibold text-lais-text-muted">
              Observações internas
            </span>
            <textarea
              v-model="registerFields.internalNotes"
              :class="textareaClass"
            />
          </label>
          <label class="grid gap-1">
            <span class="text-[11px] font-semibold text-lais-text-muted">
              Informações sensíveis
            </span>
            <textarea
              v-model="registerFields.sensitiveNotes"
              :class="textareaClass"
            />
          </label>
        </div>
      </section>

      <section
        v-show="activeRegistrationStep === 'team'"
        class="rounded-lg border border-lais-border bg-white p-4"
      >
        <div class="mb-3 flex items-center gap-2">
          <span
            class="grid size-8 place-items-center rounded-full bg-violet-50 text-violet-700 ring-1 ring-violet-100"
          >
            <span class="i-lucide-users-round size-4" aria-hidden="true" />
          </span>
          <h3 class="text-[13px] font-bold text-lais-text">Opcionistas</h3>
        </div>

        <div
          class="mb-4 rounded-lais border border-yellow-100 bg-yellow-50 px-4 py-3 text-[12px] font-semibold text-yellow-800"
        >
          Marlon Reis entra automaticamente no laudo, nunca como principal, e
          fica com no mínimo 25%. Os percentuais dos demais corretores podem ser
          ajustados quando houver mais de um corretor além do Marlon.
        </div>

        <div
          v-if="optionistAllocationErrors.length"
          class="mb-4 rounded-lais border border-red-100 bg-red-50 px-4 py-3 text-[12px] font-semibold text-red-700"
        >
          <p v-for="error in visibleOptionistAllocationErrors" :key="error">
            {{ error }}
          </p>
        </div>

        <div v-if="isLoadingOptionists" class="grid place-items-center py-8">
          <span
            class="i-lucide-loader-circle size-6 animate-spin text-lais-primary"
            aria-hidden="true"
          />
          <span class="mt-2 text-[12px] text-lais-text-muted">
            Carregando opcionistas...
          </span>
        </div>

        <template v-else>
          <label
            class="grid gap-1 text-[12px] font-semibold text-lais-text-muted"
          >
            Opcionista principal
            <select v-model="selectedPrimaryOptionistId" :class="selectClass">
              <option value="">Selecione o corretor principal</option>
              <option
                v-for="optionist in primaryOptionistOptions"
                :key="optionist.externalUserId"
                :value="optionist.externalUserId"
              >
                {{ optionist.name }} · {{ optionist.statusLabel }}
              </option>
            </select>
          </label>

          <div class="mt-4 grid gap-2">
            <label
              class="grid gap-1 text-[12px] font-semibold text-lais-text-muted"
            >
              Adicionar outros corretores
              <input
                v-model="optionistSearch"
                :class="inputClass"
                placeholder="Buscar por nome, e-mail ou filial"
              />
            </label>
            <div
              class="max-h-52 overflow-auto rounded-lais border border-lais-border bg-lais-bg-page p-2"
            >
              <button
                v-for="optionist in filteredExtraOptionists"
                :key="optionist.externalUserId"
                type="button"
                class="flex w-full items-center gap-3 rounded-lais-sm px-3 py-2 text-left transition"
                :class="
                  isExtraSelected(optionist)
                    ? 'bg-white text-lais-primary shadow-lais-soft'
                    : optionist.canRegister
                      ? 'hover:bg-white'
                      : 'cursor-not-allowed opacity-50'
                "
                :disabled="!optionist.canRegister"
                @click="toggleExtraOptionist(optionist)"
              >
                <span
                  class="grid size-9 shrink-0 place-items-center rounded-full bg-white text-[12px] font-bold text-lais-primary ring-1 ring-lais-border"
                >
                  {{ optionist.name?.slice(0, 2).toUpperCase() }}
                </span>
                <span class="min-w-0 flex-1">
                  <span class="block truncate text-[13px] font-semibold">
                    {{ optionist.name }}
                  </span>
                  <span class="block truncate text-[11px] text-lais-text-muted">
                    {{
                      optionist.externalStoreName ||
                      optionist.midasBranchName ||
                      'Sem filial'
                    }}
                    · {{ optionist.statusLabel }}
                    <template v-if="!optionist.canRegister">
                      · sem mapeamento Midas
                    </template>
                  </span>
                </span>
                <span
                  class="size-4"
                  :class="
                    isExtraSelected(optionist)
                      ? 'i-lucide-circle-check'
                      : 'i-lucide-circle'
                  "
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>

          <div
            class="mt-4 rounded-lais border border-lais-border bg-lais-bg-page p-4"
          >
            <div
              class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
            >
              <p class="text-[12px] font-bold uppercase text-lais-text-muted">
                Prévia da divisão
              </p>
              <span
                class="w-fit rounded-full bg-white px-2 py-0.5 text-[11px] font-bold text-lais-text"
              >
                {{ optionistAllocationPreview.length }} opcionistas
              </span>
            </div>
            <div
              v-if="!optionistAllocationPreview.length"
              class="mt-3 rounded-lais-sm bg-white px-3 py-3 text-[12px] text-lais-text-muted"
            >
              Selecione o corretor principal para ver a divisão antes de
              confirmar.
            </div>
            <div v-else class="mt-3 grid gap-2 sm:grid-cols-2">
              <div
                v-for="row in optionistAllocationPreview"
                :key="row.optionist.externalUserId"
                class="grid grid-cols-[32px_minmax(0,1fr)_96px] items-center gap-3 rounded-lais-sm bg-white px-3 py-2"
              >
                <span
                  class="grid size-8 place-items-center rounded-full bg-lais-primary-50 text-[12px] font-bold text-lais-primary"
                >
                  {{ row.position }}
                </span>
                <span class="min-w-0">
                  <span
                    class="block truncate text-[13px] font-semibold text-lais-text"
                  >
                    {{ row.optionist.name }}
                  </span>
                  <span class="block truncate text-[11px] text-lais-text-muted">
                    {{
                      row.position === 1
                        ? 'Principal'
                        : row.optionist.isMarlon
                          ? 'Marlon obrigatório'
                          : 'Opcionista'
                    }}
                  </span>
                </span>
                <label
                  class="relative flex items-center justify-end text-right"
                >
                  <input
                    v-if="isOptionistPercentageEditable(row)"
                    :value="
                      manualOptionistPercentages[
                        String(row.optionist.externalUserId)
                      ]
                    "
                    type="number"
                    min="0.01"
                    max="75"
                    step="0.01"
                    class="h-9 w-20 rounded-lais-sm border border-lais-border bg-white pr-6 text-right text-[13px] font-bold text-lais-text outline-none focus:border-lais-primary focus:ring-2 focus:ring-lais-primary-50"
                    @input="
                      setOptionistPercentage(
                        row.optionist.externalUserId,
                        $event.target.value
                      )
                    "
                  />
                  <strong
                    v-else
                    class="w-20 text-right text-[14px] text-lais-text"
                  >
                    {{ formatPercent(row.percentage) }}
                  </strong>
                  <span
                    class="pointer-events-none absolute right-1 text-[11px] font-bold text-lais-text-muted"
                  >
                    %
                  </span>
                </label>
              </div>
            </div>
          </div>
        </template>
      </section>
    </div>

    <template #footer>
      <div class="grid w-full gap-3 border-t border-lais-border pt-4">
        <div
          class="flex flex-col gap-2 rounded-lg bg-lais-bg-page px-3 py-2 sm:flex-row sm:items-center sm:justify-between"
        >
          <div class="min-w-0">
            <p class="text-[11px] font-bold uppercase text-lais-text-muted">
              Etapa {{ activeRegistrationStepIndex + 1 }} de
              {{ registrationSteps.length }}
            </p>
            <p class="truncate text-[13px] font-bold text-lais-text">
              {{ activeRegistrationStepConfig.label }}
            </p>
          </div>
          <button
            v-if="!canConfirmLaudoRegistration && firstRegistrationIssueStep"
            type="button"
            class="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-red-100 bg-white px-3 text-[12px] font-bold text-red-700 transition hover:bg-red-50"
            @click="goToFirstRegistrationIssue"
          >
            <span class="i-lucide-circle-alert size-4" aria-hidden="true" />
            Ver pendências
          </button>
        </div>

        <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
          <button
            type="button"
            class="inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-lg border border-lais-border bg-white px-4 text-[13px] font-bold text-lais-text transition hover:border-lais-primary hover:text-lais-primary disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="isRegisteringLaudo"
            @click="cancelRegisterDialog"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-lg border border-lais-border bg-white px-4 text-[13px] font-bold text-lais-text transition hover:border-lais-primary hover:text-lais-primary disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="isFirstRegistrationStep || isRegisteringLaudo"
            @click="goToPreviousRegistrationStep"
          >
            <span class="i-lucide-chevron-left size-4" aria-hidden="true" />
            Voltar
          </button>
          <button
            v-if="!isLastRegistrationStep"
            type="button"
            class="inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-lg border border-lais-primary bg-lais-primary px-4 text-[13px] font-bold text-white transition hover:bg-lais-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="isRegisteringLaudo"
            @click="goToNextRegistrationStep"
          >
            Avançar
            <span class="i-lucide-chevron-right size-4" aria-hidden="true" />
          </button>
          <button
            v-else
            type="submit"
            class="inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-lg border border-lais-primary bg-lais-primary px-4 text-[13px] font-bold text-white transition hover:bg-lais-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="!canConfirmLaudoRegistration"
          >
            <span
              class="size-4"
              :class="
                isRegisteringLaudo
                  ? 'i-lucide-loader-circle animate-spin'
                  : 'i-lucide-send'
              "
              aria-hidden="true"
            />
            Cadastrar no Midas
          </button>
        </div>
      </div>
    </template>
  </Dialog>
</template>
